import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import {
  History,
  ArrowUpCircle,
  ArrowDownCircle,
  Filter,
  Download,
  RefreshCw,
  AlertTriangle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value, currency = "GBP") => {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: currency === "USD" ? "USD" : "GBP",
    minimumFractionDigits: 2,
  }).format(value);
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const TradeHistory = () => {
  const { isPaperTrading } = useOutletContext();
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [stocks, setStocks] = useState([]);
  const [tradeDialogOpen, setTradeDialogOpen] = useState(false);
  const [selectedStock, setSelectedStock] = useState("");
  const [tradeAction, setTradeAction] = useState("BUY");
  const [tradeShares, setTradeShares] = useState("");
  const [executing, setExecuting] = useState(false);

  useEffect(() => {
    fetchTrades();
    fetchStocks();
  }, []);

  const fetchTrades = async () => {
    try {
      const response = await axios.get(`${API}/trades`);
      setTrades(response.data.trades || []);
    } catch (error) {
      console.error("Error fetching trades:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStocks = async () => {
    try {
      const response = await axios.get(`${API}/stocks/available`);
      setStocks(response.data.stocks || []);
    } catch (error) {
      console.error("Error fetching stocks:", error);
    }
  };

  const executeTrade = async () => {
    if (!selectedStock || !tradeShares || parseFloat(tradeShares) <= 0) {
      toast.error("Please fill in all fields correctly");
      return;
    }

    setExecuting(true);
    try {
      await axios.post(`${API}/trades`, {
        symbol: selectedStock,
        action: tradeAction,
        shares: parseFloat(tradeShares),
      });
      toast.success(`${tradeAction} order executed successfully`);
      setTradeDialogOpen(false);
      setSelectedStock("");
      setTradeShares("");
      fetchTrades();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Trade execution failed");
    } finally {
      setExecuting(false);
    }
  };

  const filteredTrades = trades.filter((trade) => {
    if (filter === "all") return true;
    return trade.action === filter;
  });

  const totalBuys = trades.filter((t) => t.action === "BUY").reduce((sum, t) => sum + t.total_value, 0);
  const totalSells = trades.filter((t) => t.action === "SELL").reduce((sum, t) => sum + t.total_value, 0);

  if (loading) {
    return (
      <div className="p-6 lg:p-8 space-y-6" data-testid="trade-history-loading">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-[400px]" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-slide-up" data-testid="trade-history-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Trade History
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isPaperTrading ? "Paper Trading Mode" : "Live Trading Mode"} • {trades.length} total trades
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchTrades} data-testid="refresh-trades-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={tradeDialogOpen} onOpenChange={setTradeDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="new-trade-btn">
                New Trade
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="trade-dialog">
              <DialogHeader>
                <DialogTitle>Execute Trade</DialogTitle>
                <DialogDescription>
                  {isPaperTrading ? (
                    <span className="flex items-center gap-1 text-warning">
                      <AlertTriangle className="w-4 h-4" />
                      Paper Trading Mode - No real money involved
                    </span>
                  ) : (
                    "Place a new trade order"
                  )}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Stock</Label>
                  <Select value={selectedStock} onValueChange={setSelectedStock}>
                    <SelectTrigger data-testid="trade-stock-select">
                      <SelectValue placeholder="Select stock" />
                    </SelectTrigger>
                    <SelectContent>
                      {stocks.map((stock) => (
                        <SelectItem key={stock.symbol} value={stock.symbol}>
                          {stock.symbol} - {stock.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Action</Label>
                  <Select value={tradeAction} onValueChange={setTradeAction}>
                    <SelectTrigger data-testid="trade-action-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="BUY">BUY</SelectItem>
                      <SelectItem value="SELL">SELL</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Number of Shares</Label>
                  <Input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={tradeShares}
                    onChange={(e) => setTradeShares(e.target.value)}
                    placeholder="Enter shares"
                    data-testid="trade-shares-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button 
                  onClick={executeTrade} 
                  disabled={executing}
                  className={tradeAction === "BUY" ? "bg-success hover:bg-success/90" : "bg-destructive hover:bg-destructive/90"}
                  data-testid="execute-trade-btn"
                >
                  {executing ? "Executing..." : `${tradeAction} Shares`}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="card-hover" data-testid="total-trades-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">
                  Total Trades
                </p>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {trades.length}
                </p>
              </div>
              <History className="w-8 h-8 text-muted-foreground/50" />
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="total-buys-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">
                  Total Buys
                </p>
                <p className="text-2xl font-bold text-success" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {formatCurrency(totalBuys)}
                </p>
              </div>
              <ArrowUpCircle className="w-8 h-8 text-success/50" />
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="total-sells-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">
                  Total Sells
                </p>
                <p className="text-2xl font-bold text-destructive" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {formatCurrency(totalSells)}
                </p>
              </div>
              <ArrowDownCircle className="w-8 h-8 text-destructive/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Trades Table */}
      <Card data-testid="trades-table-card">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-sm uppercase tracking-widest font-medium" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Transaction History
            </CardTitle>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-[120px]" data-testid="trades-filter-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="BUY">Buys Only</SelectItem>
                  <SelectItem value="SELL">Sells Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredTrades.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No trades yet</p>
              <p className="text-xs mt-1">Execute your first trade to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Symbol</th>
                    <th>Action</th>
                    <th>Shares</th>
                    <th>Price</th>
                    <th>Total</th>
                    <th>Mode</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTrades.map((trade) => (
                    <tr key={trade.id} data-testid={`trade-row-${trade.id}`}>
                      <td className="text-sm text-muted-foreground">
                        {formatDate(trade.timestamp)}
                      </td>
                      <td>
                        <div className="flex flex-col">
                          <span className="font-medium">{trade.symbol}</span>
                          <span className="text-xs text-muted-foreground">{trade.name}</span>
                        </div>
                      </td>
                      <td>
                        <Badge className={trade.action === "BUY" ? "bg-success/20 text-success" : "bg-destructive/20 text-destructive"}>
                          {trade.action}
                        </Badge>
                      </td>
                      <td className="tabular-nums">{trade.shares}</td>
                      <td className="tabular-nums">{formatCurrency(trade.price)}</td>
                      <td className="tabular-nums font-medium">{formatCurrency(trade.total_value)}</td>
                      <td>
                        <Badge variant="outline" className={trade.is_paper ? "text-warning border-warning/30" : "text-success border-success/30"}>
                          {trade.is_paper ? "Paper" : "Live"}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Disclaimer */}
      <div className="text-center py-4 border-t border-border">
        <p className="text-xs text-muted-foreground">
          {isPaperTrading ? (
            <span className="flex items-center justify-center gap-1">
              <AlertTriangle className="w-3 h-3 text-warning" />
              Paper trading mode - trades are simulated and do not involve real money
            </span>
          ) : (
            "Live trading mode - trades are executed with real funds"
          )}
        </p>
      </div>
    </div>
  );
};

export default TradeHistory;
